import { useAppSelector } from '../store/hooks';

export function hasPermission(
  requiredPermission: string,
  userPermissions: string[] = [],
  userRoles: (string | any)[] = []
): boolean {
  if (!requiredPermission) return true;

  // Normalize user roles to array of strings
  const roleStrings = userRoles.map((r) =>
    (typeof r === 'object' && r ? r.name || '' : String(r || '')).trim()
  );

  // Super-admin override check
  const isSuperAdmin = roleStrings.some((rStr) => {
    const upper = rStr.toUpperCase();
    return (
      upper === 'ADMIN' ||
      upper === 'SUPER_ADMIN' ||
      upper === 'SUPERADMIN' ||
      upper === 'SYSTEM ADMIN' ||
      upper === 'SYSTEMADMIN' ||
      upper.includes('ADMIN')
    );
  });

  if (isSuperAdmin) return true;

  // Wildcard all permissions check
  if (
    userPermissions.includes('*') ||
    userPermissions.includes('all') ||
    userPermissions.includes('ALL')
  ) {
    return true;
  }

  // Exact match
  if (userPermissions.includes(requiredPermission)) {
    return true;
  }

  // Prefix wildcard check: e.g. "roles.*" matches "roles.view"
  const parts = requiredPermission.split('.');
  if (parts.length > 1) {
    const resourcePrefix = `${parts[0]}.*`;
    if (userPermissions.includes(resourcePrefix)) {
      return true;
    }
  }

  return false;
}

export function hasRole(
  requiredRole: string | string[],
  userRoles: (string | any)[] = []
): boolean {
  if (!requiredRole) return true;
  const rolesToCheck = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
  const userRoleStrings = userRoles.map((r) =>
    (typeof r === 'object' && r ? r.name || '' : String(r || '')).toUpperCase().trim()
  );

  return rolesToCheck.some((req) => userRoleStrings.includes(req.toUpperCase().trim()));
}

export function usePermission(requiredPermission: string): boolean {
  const permissions = useAppSelector((state) => state.auth.permissions) || [];
  const roles = useAppSelector((state) => state.auth.roles) || [];
  const profile = useAppSelector((state) => state.auth.profile);

  // Extract roles from profile if state.auth.roles is empty
  const combinedRoles =
    roles.length > 0
      ? roles
      : profile?.roles || (profile?.role ? [profile.role] : []);

  return hasPermission(requiredPermission, permissions, combinedRoles);
}

export function useRole(requiredRole: string | string[]): boolean {
  const roles = useAppSelector((state) => state.auth.roles) || [];
  const profile = useAppSelector((state) => state.auth.profile);

  const combinedRoles =
    roles.length > 0
      ? roles
      : profile?.roles || (profile?.role ? [profile.role] : []);

  return hasRole(requiredRole, combinedRoles);
}
